import PageHeader from "../components/PageHeader";
import SEOMeta from "../components/SEOMeta";

const PRACTICES = [
  {
    n: "01",
    title: "Corporate strategy",
    description:
      "We work with chief executives, boards, and shareholder principals to set the direction of the organisation. We translate ambition into a defensible plan: a clear mandate, a coherent strategy, a target operating model that can deliver it, and a sequenced roadmap that the executive can be held to.",
    deliverables: [
      "Strategy on a page and supporting strategic plan, costed and sequenced.",
      "Mandate articulation aligned to shareholder or principal expectations.",
      "Target operating model, including organisation, decision rights, and accountability.",
      "Strategy execution dashboard and review cadence."
    ]
  },
  {
    n: "02",
    title: "Public-sector and SOE advisory",
    description:
      "We support state-owned enterprises, parastatals, ministries, and reform programmes on questions of mandate, governance, and value. Our work is grounded in the public-sector reality: strategy must respect the principal-agent relationship between organisation and state, the constraints of public finance, and the legitimate expectations of citizens.",
    deliverables: [
      "Mandate and statutory review, including alignment to sector policy.",
      "Governance and board effectiveness reviews.",
      "Performance contracts, key performance indicators, and reporting frameworks.",
      "Value-for-money assessments and fiscal contribution reviews.",
      "Reform programme design and implementation support."
    ]
  },
  {
    n: "03",
    title: "Resource-sector strategy",
    description:
      "We advise mining, oil and gas, and extractive-adjacent companies on commercial and operating strategy. Our perspective combines the discipline of capital-intensive industries with an understanding of the African resource economy: licence regimes, local content, community and environmental obligations, and the long timeframes that distinguish this sector from any other.",
    deliverables: [
      "Commercial strategy and route-to-market for resource producers.",
      "Asset portfolio reviews and capital-allocation frameworks.",
      "Local content strategy and supplier-development planning.",
      "Operating model design for resource companies and joint ventures."
    ]
  },
  {
    n: "04",
    title: "Performance and transformation",
    description:
      "Strategy that is not delivered is not strategy. We support transformation programmes that translate plans into operational change — restructuring, cost programmes, productivity, and the disciplined programme management that delivery requires.",
    deliverables: [
      "Operating cost diagnostics and reduction programmes.",
      "Restructuring plans and organisation redesign.",
      "Programme management office set-up and run-state support.",
      "Productivity and operational excellence reviews."
    ]
  },
  {
    n: "05",
    title: "Commercial advisory",
    description:
      "We support our clients on the transactions and partnerships that strategy depends on — without acting as a placement agent or transaction broker. Our role is to make the commercial logic clear, the structure sound, and the negotiation defensible.",
    deliverables: [
      "Investment appraisal and business-case development.",
      "Partnership and joint-venture structuring advice.",
      "Commercial due diligence support.",
      "Negotiation preparation and strategy advice."
    ]
  }
];

export default function Practice() {
  return (
    <div data-testid="practice-page">
      <SEOMeta
        title="Our Practice"
        description="Explore Dugavu Ventures' global consulting practice — corporate strategy, public-sector and SOE advisory, and resource-sector strategy for minerals, mining, and oil and gas clients."
        url="/practice"
      />
      <PageHeader
        eyebrow="Practice"
        title="Five connected practice lines."
        lede="Dugavu Ventures advises across five connected practice lines. We are deliberately specialist: our work concentrates where mandate, governance, and commercial strategy meet."
        image="https://images.pexels.com/photos/3906592/pexels-photo-3906592.jpeg?auto=compress&cs=tinysrgb&w=1800&q=80"
        imageAlt="Modern boardroom with leather chairs"
        testid="practice-header"
      />

      <section className="py-20 md:py-28">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          {PRACTICES.map((p, i) => (
            <article
              key={p.n}
              className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-16 md:py-20 ${i !== 0 ? "border-t border-rule" : ""}`}
              data-testid={`practice-${p.n}`}
            >
              <div className="md:col-span-3">
                <div className="numeral text-4xl">{p.n}</div>
                <div className="eyebrow mt-6">Practice line {p.n}</div>
              </div>
              <div className="md:col-span-9">
                <h2 className="display-2 text-3xl md:text-4xl lg:text-5xl text-ink leading-tight">{p.title}</h2>
                <p className="mt-6 text-lg text-charcoal leading-relaxed max-w-3xl">{p.description}</p>

                <div className="mt-10">
                  <div className="text-[11px] tracking-widest uppercase font-mono text-slate mb-5">Typical deliverables</div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 marker-line">
                    {p.deliverables.map((d) => (
                      <li key={d} className="text-charcoal text-[15px] leading-relaxed">{d}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}

          <div className="mt-12 pt-10 border-t border-rule" data-testid="practice-rfp-note">
            <p className="text-charcoal text-[15px] md:text-base leading-relaxed">
              To request a capability statement against a specific mandate, write to{" "}
              <a
                href="mailto:rfp@dugavuventures.com"
                data-testid="practice-rfp-link"
                className="text-oxblood link-rule"
              >
                rfp@dugavuventures.com
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
